/**
 * Centralized Error Handling Service
 *
 * Provides consistent error handling across the frontend application
 * with user-friendly error messages and proper error categorization.
 */
import { toUserFacingMessage } from '../utils/user-facing-api-error';
export var ErrorCategory;
(function (ErrorCategory) {
    ErrorCategory["AI_GENERATION"] = "AI_GENERATION";
    ErrorCategory["VALIDATION"] = "VALIDATION";
    ErrorCategory["NETWORK"] = "NETWORK";
    ErrorCategory["DATABASE"] = "DATABASE";
    ErrorCategory["AUTHENTICATION"] = "AUTHENTICATION";
    ErrorCategory["PERMISSION"] = "PERMISSION";
    ErrorCategory["CONFIGURATION"] = "CONFIGURATION";
    ErrorCategory["UNKNOWN"] = "UNKNOWN";
})(ErrorCategory || (ErrorCategory = {}));
/**
 * Error codes for different types of failures
 */
export const ERROR_CODES = {
    // AI Generation Errors
    AI_GENERATION_FAILED: 'AI_GENERATION_FAILED',
    AI_VALIDATION_FAILED: 'AI_VALIDATION_FAILED',
    AI_WORKER_UNAVAILABLE: 'AI_WORKER_UNAVAILABLE',
    AI_INVALID_RESPONSE: 'AI_INVALID_RESPONSE',
    // Validation Errors
    MISSING_PARAMETERS: 'MISSING_PARAMETERS',
    INVALID_QUESTION_TYPE: 'INVALID_QUESTION_TYPE',
    INVALID_DIFFICULTY: 'INVALID_DIFFICULTY',
    INVALID_SUBJECT: 'INVALID_SUBJECT',
    INVALID_CLASSROOM: 'INVALID_CLASSROOM',
    // Network Errors
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',
    SERVER_ERROR: 'SERVER_ERROR',
    // Database Errors
    DATABASE_CONNECTION_ERROR: 'DATABASE_CONNECTION_ERROR',
    DATABASE_QUERY_ERROR: 'DATABASE_QUERY_ERROR',
    // Authentication Errors
    UNAUTHORIZED: 'UNAUTHORIZED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    // Permission Errors
    INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
    ACCESS_DENIED: 'ACCESS_DENIED',
    // Additional Error Codes
    UNKNOWN: 'UNKNOWN',
    VALIDATION_FAILED: 'VALIDATION_FAILED'
};
/**
 * User-friendly error messages
 */
const USER_MESSAGES = {
    [ERROR_CODES.AI_GENERATION_FAILED]: 'Failed to generate quiz questions. Please try again or contact support if the issue persists.',
    [ERROR_CODES.AI_VALIDATION_FAILED]: 'The generated questions did not meet quality standards. Please try again.',
    [ERROR_CODES.AI_WORKER_UNAVAILABLE]: 'The AI service is temporarily unavailable. Please try again in a few minutes.',
    [ERROR_CODES.AI_INVALID_RESPONSE]: 'Received an invalid response from the AI service. Please try again.',
    [ERROR_CODES.MISSING_PARAMETERS]: 'Please fill in all required fields before generating questions.',
    [ERROR_CODES.INVALID_QUESTION_TYPE]: 'Please select a valid question type.',
    [ERROR_CODES.INVALID_DIFFICULTY]: 'Please select a valid difficulty level.',
    [ERROR_CODES.INVALID_SUBJECT]: 'Please select a valid subject.',
    [ERROR_CODES.INVALID_CLASSROOM]: 'Please select a valid classroom.',
    [ERROR_CODES.NETWORK_ERROR]: 'Network connection error. Please check your internet connection and try again.',
    [ERROR_CODES.TIMEOUT_ERROR]: 'The request timed out. Please try again.',
    [ERROR_CODES.SERVER_ERROR]: 'Server error occurred. Please try again or contact support.',
    [ERROR_CODES.DATABASE_CONNECTION_ERROR]: 'Database connection error. Please try again.',
    [ERROR_CODES.DATABASE_QUERY_ERROR]: 'Database error occurred. Please try again.',
    [ERROR_CODES.UNAUTHORIZED]: 'You are not authorized to perform this action. Please log in again.',
    [ERROR_CODES.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
    [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'You do not have permission to perform this action.',
    [ERROR_CODES.ACCESS_DENIED]: 'Access denied. Please contact your administrator.'
};
/**
 * Technical error messages for logging
 */
const TECHNICAL_MESSAGES = {
    [ERROR_CODES.AI_GENERATION_FAILED]: 'AI question generation failed',
    [ERROR_CODES.AI_VALIDATION_FAILED]: 'AI generated content failed validation',
    [ERROR_CODES.AI_WORKER_UNAVAILABLE]: 'Cloudflare Worker AI service unavailable',
    [ERROR_CODES.AI_INVALID_RESPONSE]: 'Invalid response format from AI service',
    [ERROR_CODES.MISSING_PARAMETERS]: 'Required parameters missing from request',
    [ERROR_CODES.INVALID_QUESTION_TYPE]: 'Invalid question type provided',
    [ERROR_CODES.INVALID_DIFFICULTY]: 'Invalid difficulty level provided',
    [ERROR_CODES.INVALID_SUBJECT]: 'Invalid subject provided',
    [ERROR_CODES.INVALID_CLASSROOM]: 'Invalid classroom provided',
    [ERROR_CODES.NETWORK_ERROR]: 'Network request failed',
    [ERROR_CODES.TIMEOUT_ERROR]: 'Request timeout exceeded',
    [ERROR_CODES.SERVER_ERROR]: 'Internal server error',
    [ERROR_CODES.DATABASE_CONNECTION_ERROR]: 'Database connection failed',
    [ERROR_CODES.DATABASE_QUERY_ERROR]: 'Database query failed',
    [ERROR_CODES.UNAUTHORIZED]: 'User not authenticated',
    [ERROR_CODES.TOKEN_EXPIRED]: 'Authentication token expired',
    [ERROR_CODES.INSUFFICIENT_PERMISSIONS]: 'User lacks required permissions',
    [ERROR_CODES.ACCESS_DENIED]: 'Access denied by server'
};
/**
 * Format an error into a standardized ErrorDetails object
 */
export function formatError(error, category = ErrorCategory.UNKNOWN, context) {
    const timestamp = new Date().toISOString();
    // Handle different error types
    if (typeof error === 'string') {
        return {
            category,
            code: ERROR_CODES.UNKNOWN,
            message: error,
            userMessage: 'An unexpected error occurred. Please try again.',
            timestamp,
            context
        };
    }
    if (error?.code && Object.values(ERROR_CODES).includes(error.code)) {
        return {
            category,
            code: error.code,
            message: TECHNICAL_MESSAGES[error.code] || error.message || 'Unknown error',
            userMessage: USER_MESSAGES[error.code] || 'An unexpected error occurred. Please try again.',
            technicalDetails: error.details || error.stack,
            timestamp,
            context
        };
    }
    // Handle HTTP errors
    if (error?.status || error?.response?.status) {
        const status = error.status || error.response?.status;
        let code = ERROR_CODES.SERVER_ERROR;
        let category = ErrorCategory.NETWORK;
        if (status === 401) {
            code = ERROR_CODES.UNAUTHORIZED;
            category = ErrorCategory.AUTHENTICATION;
        }
        else if (status === 403) {
            code = ERROR_CODES.ACCESS_DENIED;
            category = ErrorCategory.PERMISSION;
        }
        else if (status >= 500) {
            code = ERROR_CODES.SERVER_ERROR;
            category = ErrorCategory.NETWORK;
        }
        else if (status >= 400) {
            code = ERROR_CODES.VALIDATION_FAILED;
            category = ErrorCategory.VALIDATION;
        }
        return {
            category,
            code,
            message: TECHNICAL_MESSAGES[code] || `HTTP ${status} error`,
            userMessage: toUserFacingMessage(error, {
                fallback: USER_MESSAGES[code] || 'An error occurred. Please try again.'
            }),
            technicalDetails: error.message || error.response?.data?.message,
            timestamp,
            context: { ...context, statusCode: status }
        };
    }
    // Handle validation errors
    if (error?.errors && Array.isArray(error.errors)) {
        return {
            category: ErrorCategory.VALIDATION,
            code: ERROR_CODES.MISSING_PARAMETERS,
            message: 'Validation failed',
            userMessage: 'Please check your input and try again.',
            technicalDetails: JSON.stringify(error.errors),
            timestamp,
            context: { ...context, validationErrors: error.errors }
        };
    }
    // Default error handling
    return {
        category,
        code: ERROR_CODES.UNKNOWN,
        message: error?.message || 'Unknown error occurred',
        userMessage: toUserFacingMessage(error, {
            fallback: 'An unexpected error occurred. Please try again.'
        }),
        technicalDetails: error?.stack,
        timestamp,
        context
    };
}
/**
 * Get user-friendly error message for a specific error code
 */
export function getUserMessage(errorCode) {
    return USER_MESSAGES[errorCode] || 'An unexpected error occurred. Please try again.';
}
/**
 * Get technical error message for logging
 */
export function getTechnicalMessage(errorCode) {
    return TECHNICAL_MESSAGES[errorCode] || 'Unknown error occurred';
}
/**
 * Log error with appropriate level and context
 */
export function logError(errorDetails, additionalContext) {
    const logData = {
        category: errorDetails.category,
        code: errorDetails.code,
        message: errorDetails.message,
        timestamp: errorDetails.timestamp,
        context: { ...errorDetails.context, ...additionalContext }
    };
    // Use console.error for all errors in frontend
    console.error('[ErrorHandler]', logData);
    // In production, you might want to send errors to a logging service
    if (process.env.NODE_ENV === 'production') {
        // Example: send to logging service
        // logToService(logData);
    }
}
/**
 * Create a validation error object
 */
export function createValidationError(field, message, code = ERROR_CODES.MISSING_PARAMETERS) {
    return {
        field,
        message,
        code
    };
}
/**
 * Check if an error is a specific type
 */
export function isErrorType(error, errorCode) {
    if (typeof error === 'string')
        return false;
    return error?.code === errorCode;
}
/**
 * Check if an error is a network error
 */
export function isNetworkError(error) {
    return isErrorType(error, ERROR_CODES.NETWORK_ERROR) ||
        isErrorType(error, ERROR_CODES.TIMEOUT_ERROR) ||
        isErrorType(error, ERROR_CODES.SERVER_ERROR);
}
/**
 * Check if an error is an authentication error
 */
export function isAuthError(error) {
    return isErrorType(error, ERROR_CODES.UNAUTHORIZED) ||
        isErrorType(error, ERROR_CODES.TOKEN_EXPIRED);
}
/**
 * Check if an error is a validation error
 */
export function isValidationError(error) {
    return isErrorType(error, ERROR_CODES.MISSING_PARAMETERS) ||
        isErrorType(error, ERROR_CODES.INVALID_QUESTION_TYPE) ||
        isErrorType(error, ERROR_CODES.INVALID_DIFFICULTY) ||
        isErrorType(error, ERROR_CODES.INVALID_SUBJECT) ||
        isErrorType(error, ERROR_CODES.INVALID_CLASSROOM);
}
export default {
    formatError,
    getUserMessage,
    getTechnicalMessage,
    logError,
    createValidationError,
    isErrorType,
    isNetworkError,
    isAuthError,
    isValidationError,
    ERROR_CODES,
    ErrorCategory
};
